import axios from 'axios';

const getBaseUrl = () => process.env.VUE_APP_API_EXT_URL.split('user-api')[0];

const getAuthHeaders = () => ({
    "Content-Type": "application/json",
    "Authorization": "Bearer " + localStorage.getItem("jwt")
});

/**
 * Fetch available report filters (dates and times)
 */
export const fetchFilters = async () => {
    const response = await axios.get(`${getBaseUrl()}/dashboard/filters`, {
        headers: getAuthHeaders()
    });
    return response.data;
};

/**
 * Fetch dashboard kiosk data
 * @param {string|null} id - Optional report ID
 */
export const fetchKioskData = async (id = null) => {
    let url = `${getBaseUrl()}/dashboard`;
    if (id) url += `?id=${id}`;

    const response = await axios.get(url, {
        headers: getAuthHeaders()
    });
    return response.data;
};

/**
 * Refresh dashboard data (generate new report)
 */
export const refreshDashboard = async () => {
    const response = await axios.get(`${getBaseUrl()}/dashboard/refresh`, {
        headers: getAuthHeaders()
    });
    return response.data;
};

/**
 * Refresh Zoho Desk tickets for a specific report
 * @param {string} reportId
 */
export const refreshTickets = async (reportId) => {
    const url = `${getBaseUrl()}/dashboard/refresh-tickets?id=${reportId}`;
    await axios.put(url, null, {
        headers: { "Authorization": "Bearer " + localStorage.getItem("jwt") }
    });
};

/**
 * Export dashboard data to Excel
 * @param {string} reportId
 * @param {Array} machineIds
 */
export const exportDashboard = async (reportId, machineIds) => {
    const response = await axios.post(
        `${getBaseUrl()}/dashboard/export`,
        { reportId, machines: machineIds },
        {
            responseType: 'blob',
            headers: { 'Authorization': `Bearer ${localStorage.getItem('jwt')}` }
        }
    );
    return response.data;
};

/**
 * Fetch comments for a kiosk
 * @param {string} kioskId
 */
export const fetchComments = async (kioskId) => {
    const response = await axios.get(`${getBaseUrl()}/comment?kioskId=${kioskId}`, {
        headers: getAuthHeaders()
    });
    return response.data;
};

/**
 * Save a comment for a kiosk
 * @param {string} kioskId
 * @param {string} author
 * @param {string} content
 */
export const saveComment = async (kioskId, author, content) => {
    const response = await axios.post(
        `${getBaseUrl()}/comment`,
        { kioskId, author, content },
        { headers: getAuthHeaders() }
    );
    return response.data;
};

/**
 * Check API statuses
 * @param {Array} apis - Array of { name, url } objects
 */
export const checkApiStatuses = async (apis) => {
    const statusPromises = apis.map(async (api) => {
        try {
            await axios.get(api.url, {
                timeout: 5000,
                validateStatus: status => status === 200 || status === 401
            });
            return { ...api, status: 'En ligne' };
        } catch {
            return { ...api, status: 'Hors ligne' };
        }
    });

    return Promise.all(statusPromises);
};

/**
 * Download blob as file
 * @param {Blob} blob
 * @param {string} filename
 */
export const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

/**
 * Fetch fleet overview statistics
 * @param {string} startDate - ISO date string (YYYY-MM-DD)
 * @param {string} endDate - ISO date string (YYYY-MM-DD)
 * @param {Array} tagIds - Optional array of tag IDs
 */
export const fetchFleetOverview = async (startDate, endDate, tagIds = []) => {
    const params = new URLSearchParams({
        startDate,
        endDate
    });

    if (tagIds.length > 0) {
        params.append('tagIds', JSON.stringify(tagIds));
    }

    const response = await axios.get(`${getBaseUrl()}/dashboard/fleet-overview?${params.toString()}`, {
        headers: getAuthHeaders()
    });
    return response.data;
};

export default {
    fetchFilters,
    fetchKioskData,
    refreshDashboard,
    refreshTickets,
    exportDashboard,
    fetchComments,
    saveComment,
    checkApiStatuses,
    downloadBlob,
    fetchFleetOverview
};
